import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Download, Upload, FileText, CheckCircle2, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { apbsNameMatchingApi } from '../../services/apbs-name-matching.api';

interface BatchRow {
  id: string;
  batchId: string;
  requestFileName: string;
  responseFileName: string;
  uploadDate: string;
  uploadedBy: string;
  workStage: string;
  batchType: string;
  status: string;
  totalRecords: number;
  processedRecords: number;
  successCount: number;
  transactionFailedCount: number;
  transactionSuccessRate: number;
  exactMatchCount: number;
  highMatchCount: number;
  mismatchCount: number;
  nameMatchingSuccessRate: number;
  totalAmount: number;
  paidAmount: number;
  processingTimeMs: number;
}

const ApbsNameMatchingDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const fetchBatches = async (p = page) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apbsNameMatchingApi.listBatches({ page: p, limit: 15 });
      setBatches(data.batches);
      setTotalPages(data.pagination.totalPages);
      setTotal(data.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches(page);
  }, [page]);

  const handleDownload = async (batchId: string) => {
    setDownloadingId(batchId);
    try {
      const blob = await apbsNameMatchingApi.downloadReport(batchId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `NameMatching_${batchId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download report');
    } finally {
      setDownloadingId(null);
    }
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'PROCESSING':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'FAILED':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      COMPLETED: 'bg-green-100 text-green-700',
      PROCESSING: 'bg-yellow-100 text-yellow-700',
      FAILED: 'bg-red-100 text-red-700',
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
        {statusIcon(status)}
        {status}
      </span>
    );
  };

  // Summary stats from loaded batches
  const completedBatches = batches.filter(b => b.status === 'COMPLETED');
  const totalRecordsAll = completedBatches.reduce((s, b) => s + b.totalRecords, 0);
  const avgMatchRate = completedBatches.length > 0
    ? (completedBatches.reduce((s, b) => s + Number(b.nameMatchingSuccessRate), 0) / completedBatches.length).toFixed(1)
    : '0.0';

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Name Matching Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">{total} batch{total !== 1 ? 'es' : ''} processed</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => fetchBatches(page)}
            className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => navigate('/ApbsNameMatchingUpload')}
            className="px-4 py-2 text-sm bg-gradient-to-r from-indigo-500 to-blue-600 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-blue-700 flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            New Upload
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 font-medium">Total Batches</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{total}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 font-medium">Completed</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{completedBatches.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 font-medium">Total Records</p>
          <p className="text-2xl font-bold text-indigo-600 mt-1">{totalRecordsAll.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 font-medium">Avg Match Rate</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">{avgMatchRate}%</p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-500 mr-2" />
            <span className="text-gray-500">Loading batches...</span>
          </div>
        ) : batches.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No batches yet</p>
            <p className="text-sm text-gray-400 mt-1">Upload REQUEST + RESPONSE files to get started</p>
            <button
              onClick={() => navigate('/ApbsNameMatchingUpload')}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700"
            >
              Upload Files
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Batch ID</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Upload Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Stage</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Status</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Records</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Txn Success</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Name Match</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Exact</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Mismatch</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((batch) => (
                    <tr key={batch.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="font-medium text-indigo-600">{batch.batchId}</div>
                        <div className="text-xs text-gray-400 truncate max-w-[200px]" title={batch.responseFileName}>
                          {batch.responseFileName}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-700">
                        {new Date(batch.uploadDate).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                        <div className="text-xs text-gray-400">{batch.uploadedBy}</div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                          {batch.workStage || '-'}
                        </span>
                      </td>
                      <td className="py-3 px-4">{statusBadge(batch.status)}</td>
                      <td className="text-right py-3 px-4 font-medium">{batch.totalRecords.toLocaleString()}</td>
                      <td className="text-right py-3 px-4">
                        <span className={`font-semibold ${Number(batch.transactionSuccessRate) >= 90 ? 'text-green-600' : Number(batch.transactionSuccessRate) >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {Number(batch.transactionSuccessRate).toFixed(1)}%
                        </span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className={`font-semibold ${Number(batch.nameMatchingSuccessRate) >= 50 ? 'text-blue-600' : 'text-orange-600'}`}>
                          {Number(batch.nameMatchingSuccessRate).toFixed(1)}%
                        </span>
                      </td>
                      <td className="text-right py-3 px-4 text-green-600 font-medium">{batch.exactMatchCount.toLocaleString()}</td>
                      <td className="text-right py-3 px-4 text-red-600 font-medium">{batch.mismatchCount.toLocaleString()}</td>
                      <td className="text-center py-3 px-4">
                        {batch.status === 'COMPLETED' && (
                          <button
                            onClick={() => handleDownload(batch.batchId)}
                            disabled={downloadingId === batch.batchId}
                            className="px-3 py-1.5 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-lg hover:bg-indigo-100 flex items-center gap-1 mx-auto disabled:opacity-50"
                          >
                            {downloadingId === batch.batchId ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Download className="w-3 h-3" />
                            )}
                            Excel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Page {page} of {totalPages} ({total} total)
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page <= 1}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page >= totalPages}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ApbsNameMatchingDashboard;
